/**
 * Hook to fetch overview metrics data
 */

import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSalonStore } from "@/lib/store/salon";
import { GET_COMPREHENSIVE_REPORT } from "../graphql/report-queries";
import { formatReportDate } from "../lib/report-date-range";
import { useReportsV2Store } from "../store/use-reports-v2-store";
import { createComparableValue } from "../types";
import type { OverviewData } from "../types/overview-types";

export function useOverviewData() {
  const { currentSalon } = useSalonStore();
  const { currentPeriod, comparisonPeriod } = useReportsV2Store();

  // Fetch current period data
  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: currentPeriod
          ? formatReportDate(currentPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: currentPeriod
          ? formatReportDate(currentPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !currentPeriod,
  });

  // Fetch comparison period data
  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !comparisonPeriod,
  });

  const loading = currentLoading || comparisonLoading;
  const error = currentError || comparisonError;
  const isReady = !(!currentSalon || !currentSalon.timezone || !currentPeriod || !comparisonPeriod);

  // Transform GraphQL data into AbsoluteMetric format
  const overviewData: OverviewData | null = useMemo(() => {
    if (!isReady) return null;
    if (!currentData?.comprehensiveReport || !comparisonData?.comprehensiveReport) return null;

    const current = currentData.comprehensiveReport;
    const comparison = comparisonData.comprehensiveReport;

    // Extract trend data from daily breakdown for financial metrics
    const revenueTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.revenue || 0,
        date: day.date,
      })) || [];

    const avgTransactionTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.completedCount > 0 ? (day.revenue || 0) / day.completedCount : 0,
        date: day.date,
      })) || [];

    const totalTransactionsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.completedCount || 0,
        date: day.date,
      })) || [];

    const totalBookingsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.totalAppointmentCount || 0,
        date: day.date,
      })) || [];

    const completedBookingsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.completedCount || 0,
        date: day.date,
      })) || [];

    const cancelledBookingsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.cancelledCount || 0,
        date: day.date,
      })) || [];

    const noShowBookingsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.noShowCount || 0,
        date: day.date,
      })) || [];

    const pendingBookingsTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value: day.pendingCount || 0,
        date: day.date,
      })) || [];

    const cancellationRateTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value:
          day.totalAppointmentCount > 0
            ? ((day.cancelledCount || 0) / day.totalAppointmentCount) * 100
            : 0,
        date: day.date,
      })) || [];

    const noShowRateTrend =
      current.revenue.dailyBreakdown?.map((day) => ({
        value:
          day.totalAppointmentCount > 0
            ? ((day.noShowCount || 0) / day.totalAppointmentCount) * 100
            : 0,
        date: day.date,
      })) || [];

    // Extract trend data from daily customer activity
    const newCustomersTrend =
      current.customers.dailyCustomerActivity?.map((day) => ({
        value: day.newCustomers || 0,
        date: day.date,
      })) || [];

    const returningCustomersTrend =
      current.customers.dailyCustomerActivity?.map((day) => ({
        value: day.returningCustomers || 0,
        date: day.date,
      })) || [];

    const totalCustomerActivityTrend =
      current.customers.dailyCustomerActivity?.map((day) => ({
        value: (day.newCustomers || 0) + (day.returningCustomers || 0),
        date: day.date,
      })) || [];

    // Calculate revenue per customer trend (daily revenue / daily total customers)
    const revenuePerCustomerTrend =
      current.revenue.dailyBreakdown?.map((day, index) => {
        const customerActivity = current.customers.dailyCustomerActivity?.[index];
        const totalCustomers = customerActivity
          ? (customerActivity.newCustomers || 0) + (customerActivity.returningCustomers || 0)
          : 0;
        return {
          value: totalCustomers > 0 ? (day.revenue || 0) / totalCustomers : 0,
          date: day.date,
        };
      }) || [];

    return {
      financial: {
        totalRevenue: {
          type: "absolute",
          label: "Total Revenue",
          value: {
            ...createComparableValue(current.revenue.totalRevenue, comparison.revenue.totalRevenue),
            trend: revenueTrend,
          },
          format: "currency",
        },
        // Premium financial fields
        ...(current.revenue.averageTransactionValue != null &&
        comparison.revenue.averageTransactionValue != null
          ? {
              averageTransactionValue: {
                type: "absolute" as const,
                label: "Avg Transaction",
                value: {
                  ...createComparableValue(
                    current.revenue.averageTransactionValue,
                    comparison.revenue.averageTransactionValue
                  ),
                  trend: avgTransactionTrend,
                },
                format: "currency" as const,
              },
            }
          : {}),
        ...(current.revenue.completedBookings != null &&
        comparison.revenue.completedBookings != null
          ? {
              totalTransactions: {
                type: "absolute" as const,
                label: "Total Transactions",
                value: {
                  ...createComparableValue(
                    current.revenue.completedBookings,
                    comparison.revenue.completedBookings
                  ),
                  trend: totalTransactionsTrend,
                },
                format: "number" as const,
              },
            }
          : {}),
        ...(current.revenue.revenuePerCustomer != null &&
        comparison.revenue.revenuePerCustomer != null
          ? {
              revenuePerCustomer: {
                type: "absolute" as const,
                label: "Revenue per Customer",
                value: {
                  ...createComparableValue(
                    current.revenue.revenuePerCustomer,
                    comparison.revenue.revenuePerCustomer
                  ),
                  trend: revenuePerCustomerTrend,
                },
                format: "currency" as const,
              },
            }
          : {}),
        ...(current.customers.averageCustomerLifetimeValue != null &&
        comparison.customers.averageCustomerLifetimeValue != null
          ? {
              averageCustomerLifetimeValue: {
                type: "absolute" as const,
                label: "Avg Customer Lifetime Value",
                value: createComparableValue(
                  current.customers.averageCustomerLifetimeValue,
                  comparison.customers.averageCustomerLifetimeValue
                ),
                format: "currency" as const,
              },
            }
          : {}),
        dailyRevenueTrend:
          current.revenue.dailyBreakdown?.map((day) => ({
            date: day.date,
            revenue: day.revenue || 0,
          })) ?? [],
        topServices: current.revenue.serviceBreakdown ?? null,
        revenueByCustomerType:
          current.revenue.newCustomerRevenue != null &&
          current.revenue.returningCustomerRevenue != null &&
          comparison.revenue.newCustomerRevenue != null &&
          comparison.revenue.returningCustomerRevenue != null
            ? {
                current: {
                  new: current.revenue.newCustomerRevenue,
                  returning: current.revenue.returningCustomerRevenue,
                },
                previous: {
                  new: comparison.revenue.newCustomerRevenue,
                  returning: comparison.revenue.returningCustomerRevenue,
                },
              }
            : null,
      },
      customers: {
        totalCustomers: {
          type: "absolute",
          label: "Total Customers",
          value: {
            ...createComparableValue(
              current.customers.totalCustomers,
              comparison.customers.totalCustomers
            ),
            trend: totalCustomerActivityTrend,
          },
          format: "number",
        },
        newCustomers: {
          type: "absolute",
          label: "New Customers",
          value: {
            ...createComparableValue(
              current.customers.newCustomers,
              comparison.customers.newCustomers
            ),
            trend: newCustomersTrend,
          },
          format: "number",
        },
        // Premium customer fields
        ...(current.customers.returningCustomers != null &&
        comparison.customers.returningCustomers != null
          ? {
              returningCustomers: {
                type: "absolute" as const,
                label: "Returning Customers",
                value: {
                  ...createComparableValue(
                    current.customers.returningCustomers,
                    comparison.customers.returningCustomers
                  ),
                  trend: returningCustomersTrend,
                },
                format: "number" as const,
              },
            }
          : {}),
        ...(current.customers.customerRetentionRate != null &&
        comparison.customers.customerRetentionRate != null
          ? {
              retentionRate: {
                type: "absolute" as const,
                label: "Retention Rate",
                value: createComparableValue(
                  current.customers.customerRetentionRate,
                  comparison.customers.customerRetentionRate,
                  false,
                  true // isPercentageValue
                ),
                format: "percentage" as const,
              },
            }
          : {}),
        ...(current.customers.averageVisitFrequency != null &&
        comparison.customers.averageVisitFrequency != null
          ? {
              averageVisitFrequency: {
                type: "absolute" as const,
                label: "Avg Visit Frequency",
                value: createComparableValue(
                  current.customers.averageVisitFrequency,
                  comparison.customers.averageVisitFrequency
                ),
                format: "number" as const,
              },
            }
          : {}),
        ...(current.customers.averageDaysBetweenVisits != null &&
        comparison.customers.averageDaysBetweenVisits != null
          ? {
              averageDaysBetweenVisits: {
                type: "absolute" as const,
                label: "Avg Days Between Visits",
                value: createComparableValue(
                  current.customers.averageDaysBetweenVisits,
                  comparison.customers.averageDaysBetweenVisits,
                  true // inverse: lower is better (more frequent visits)
                ),
                format: "number" as const,
                inverse: true,
              },
            }
          : {}),
        ...(current.customers.inactiveCustomers != null &&
        comparison.customers.inactiveCustomers != null
          ? {
              inactiveCustomers: {
                type: "absolute" as const,
                label: "Inactive Customers",
                value: createComparableValue(
                  current.customers.inactiveCustomers,
                  comparison.customers.inactiveCustomers,
                  true // inverse: lower is better
                ),
                format: "number" as const,
                inverse: true,
              },
            }
          : {}),
        ...(current.customers.averageCustomerTenure != null &&
        comparison.customers.averageCustomerTenure != null
          ? {
              averageCustomerTenure: {
                type: "absolute" as const,
                label: "Avg Customer Tenure",
                value: createComparableValue(
                  current.customers.averageCustomerTenure,
                  comparison.customers.averageCustomerTenure
                ),
                format: "number" as const,
              },
            }
          : {}),
        ...(current.customers.customerGrowthRate != null &&
        comparison.customers.customerGrowthRate != null
          ? {
              customerGrowthRate: {
                type: "absolute" as const,
                label: "Customer Growth Rate",
                value: createComparableValue(
                  current.customers.customerGrowthRate,
                  comparison.customers.customerGrowthRate,
                  false,
                  true // isPercentageValue
                ),
                format: "percentage" as const,
              },
            }
          : {}),
        ...(current.customers.churnRate != null && comparison.customers.churnRate != null
          ? {
              churnRate: {
                type: "absolute" as const,
                label: "Churn Rate",
                value: createComparableValue(
                  current.customers.churnRate,
                  comparison.customers.churnRate,
                  true, // inverse: lower is better
                  true // isPercentageValue
                ),
                format: "percentage" as const,
                inverse: true,
              },
            }
          : {}),
        topCustomers: current.customers.topCustomers ?? null,
        dailyCustomerActivity:
          current.customers.dailyCustomerActivity?.map((day) => ({
            date: day.date, // Keep ISO format for chart component
            newCustomers: day.newCustomers,
            returningCustomers: day.returningCustomers,
          })) || [],
        cohortRetention: current.customers.cohortRetention ?? null,
        loyaltyDistribution:
          current.customers.loyaltyDistribution != null &&
          comparison.customers.loyaltyDistribution != null
            ? {
                current: current.customers.loyaltyDistribution,
                previous: comparison.customers.loyaltyDistribution,
              }
            : null,
      },
      bookings: {
        totalBookings: {
          type: "absolute",
          label: "Total Bookings",
          value: {
            ...createComparableValue(
              current.revenue.totalBookings,
              comparison.revenue.totalBookings
            ),
            trend: totalBookingsTrend,
          },
          format: "number",
        },
        completedBookings: {
          type: "absolute",
          label: "Completed Bookings",
          value: {
            ...createComparableValue(
              current.revenue.completedBookings,
              comparison.revenue.completedBookings
            ),
            trend: completedBookingsTrend,
          },
          format: "number",
        },
        // Premium booking fields
        ...(current.revenue.cancelledBookings != null &&
        comparison.revenue.cancelledBookings != null
          ? {
              cancelledBookings: {
                type: "absolute" as const,
                label: "Cancelled Bookings",
                value: {
                  ...createComparableValue(
                    current.revenue.cancelledBookings,
                    comparison.revenue.cancelledBookings,
                    true // inverse: lower is better
                  ),
                  trend: cancelledBookingsTrend,
                },
                format: "number" as const,
                inverse: true,
              },
            }
          : {}),
        ...(current.revenue.noShowBookings != null && comparison.revenue.noShowBookings != null
          ? {
              noShowBookings: {
                type: "absolute" as const,
                label: "No-Show Bookings",
                value: {
                  ...createComparableValue(
                    current.revenue.noShowBookings,
                    comparison.revenue.noShowBookings,
                    true // inverse: lower is better
                  ),
                  trend: noShowBookingsTrend,
                },
                format: "number" as const,
                inverse: true,
              },
            }
          : {}),
        ...(current.revenue.pendingBookings != null && comparison.revenue.pendingBookings != null
          ? {
              pendingBookings: {
                type: "absolute" as const,
                label: "Pending Bookings",
                value: {
                  ...createComparableValue(
                    current.revenue.pendingBookings,
                    comparison.revenue.pendingBookings
                  ),
                  trend: pendingBookingsTrend,
                },
                format: "number" as const,
              },
            }
          : {}),
        ...(current.operations && comparison.operations
          ? {
              cancellationRate: {
                type: "absolute" as const,
                label: "Cancellation Rate",
                value: {
                  ...createComparableValue(
                    current.operations.appointmentCancellationRate,
                    comparison.operations.appointmentCancellationRate,
                    true, // inverse: lower is better
                    true // isPercentageValue
                  ),
                  trend: cancellationRateTrend,
                },
                format: "percentage" as const,
                inverse: true,
              },
              noShowRate: {
                type: "absolute" as const,
                label: "No-Show Rate",
                value: {
                  ...createComparableValue(
                    current.operations.noShowRate,
                    comparison.operations.noShowRate,
                    true, // inverse: lower is better
                    true // isPercentageValue
                  ),
                  trend: noShowRateTrend,
                },
                format: "percentage" as const,
                inverse: true,
              },
            }
          : {}),
      },
    };
  }, [currentData, comparisonData, isReady]);

  if (!isReady) {
    return { data: null, loading: false, error: null };
  }

  return {
    data: overviewData,
    loading,
    error,
  };
}
